import React from 'react'
import { arrayOf, string, bool } from 'prop-types'
import * as yup from 'yup'
import Form from 'react-formal'
import Moment from 'moment'
import momentLocalizer from 'react-widgets-moment'
import { Link } from 'gatsby'
import { Flex, Box, Heading, Text } from 'rebass'
import { request } from 'graphql-request'
import {
  Label,
  CenteredContentBox,
  Message,
  SubmitButton,
  Input,
  Fieldset,
  Widget,
  Checkbox
} from './components'
import { trackEvent } from '../../../lib/tracker'
import permissions from '../../../lib/permission/schema'
import permissionsGraphqlMutation from '../../../lib/permission/graphql-mutation'
import PermissionControls from './components/PermissionControls'
import enquiryTypes from './fixtures/enquiry-types'

Moment.locale('en-gb')
momentLocalizer()

const modelSchema = yup.object({
  email: yup
    .string()
    .default('')
    .required('Please enter an email address')
    .email('Please check your email address is correct')
    .max(255, 'Your email is over 255 characters'),
  firstName: yup
    .string()
    .default('')
    .required('Please enter your first name')
    .max(75, 'Your name is over 75 characters'),
  lastName: yup
    .string()
    .default('')
    .required('Please enter your last name')
    .max(75, 'Your last name is over 75 characters'),
  phone: yup
    .string()
    .required('Please enter a phone number')
    .min(11, 'Please check your number')
    .max(16, 'Your number is too long'),
  location: yup
    .string()
    .nullable()
    .default('')
    .required('Please choose a location'),
  postcode: yup
    .string()
    .matches(/^[A-Z]{1,2}[0-9]{1,2}[A-Z]? ?[0-9][A-Z]{2}$/i, 'Invalid postcode')
    .required('Please enter your postcode'),
  date: yup
    .date()
    .min(new Date(), "You can't book in the past")
    .required('Please choose a date'),
  enquiryType: yup.string().oneOf(enquiryTypes).default(enquiryTypes[1]),
  permissions: permissions(['email', 'sms', 'phone']),
  termsAndConditions: yup
    .bool()
    .test(
      'terms-agreed',
      'You must agree to the terms and conditions',
      (v) => !!v
    )
})

Form.addInputTypes(require('react-formal-inputs'))

const submitForm = (data) => {
  const mutation = `mutation {
    createTestDrive(testDriveRequest: {
      firstName: "${data.firstName}",
      lastName: "${data.lastName}",
      email: "${data.email}",
      phone: "${data.phone}",
      postcode: "${data.postcode}",
      location: "${data.location}",
      date: "${data.date.toISOString()}",
      vehicleId: "${data.vehicleId}",
      isNewVehicle: ${!!data.newVehicle},
      enquiryType: "${data.enquiryType}",
      sourceUrl: "${document.location.toString()}",
      ${permissionsGraphqlMutation()(data)}
    }) {
      email
      sourceUrl
      createdDate
    }
  }`
  return request(`${process.env.GATSBY_API_URL}/graphql`, mutation)
}

const determineTrackEvent = (isNewVehicle, isCar) => {
  const age = isNewVehicle ? 'New' : 'Used'
  const type = isCar ? 'Car' : 'Van'
  return `${age}${type}RegisterInterest`
}

class FormTestDrive extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      submitSuccess: false,
      submitError: false
    }
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  async handleSubmit(formData) {
    const isCar = window.location.pathname.includes('cars')
    const event = determineTrackEvent(this.props.newVehicle, isCar)

    trackEvent(event, 'Form', 'Submit', '', {
      location: formData.location,
      enquiryType: formData.enquiryType,
      vehicleId: this.props.vehicleId,
      make: this.props.brand,
      model: this.props.model
    })
    const response = await submitForm({
      ...formData,
      vehicleId: this.props.vehicleId,
      newVehicle: this.props.newVehicle
    })
    if (response && response.createTestDrive) {
      this.setState({ submitSuccess: true })
    } else {
      trackEvent(event, 'Form', 'Error')
      this.setState({ submitError: true })
    }
  }

  render() {
    if (this.state.submitSuccess) {
      trackEvent(determineTrackEvent(this.props.newVehicle), 'Form', 'Thanks')
      return (
        <div>
          <Heading as="h3" mb={2}>
            Thank you for registering your interest with Citygate.
          </Heading>
          <Text>
            You will receive an email from a member of our Sales Team to discuss
            this further.
          </Text>
        </div>
      )
    }

    const singleLocation = this.props.locations.length === 1
    const defaultFormValue = {
      location: singleLocation ? this.props.locations[0].location : null,
      enquiryType: enquiryTypes[1]
    }

    return (
      <Form
        schema={modelSchema}
        className="f24form"
        onSubmit={this.handleSubmit}
        data-f24name="book-test-drive-form"
        defaultValue={defaultFormValue || {}}
      >
        {this.state.submitError && (
          <Heading as="h3">There has been an problem!</Heading>
        )}
        <Fieldset>
          <Flex flexWrap="wrap" width={'100%'} py={[10]}>
            <Box
              width={[12 / 12, null, 5 / 12]}
              mr={[0, null, (2 / 12) * 100 + '%']}
              mb={10}
            >
              <Label>First name*</Label>
              <Input
                placeholder="Enter first name"
                name="firstName"
                aria-label="You are required to enter your first name"
              />
              <Message for="firstName" />
            </Box>
            <Box width={[12 / 12, null, 5 / 12]} mb={10}>
              <Label>Last name*</Label>
              <Input
                placeholder="Enter last name"
                name="lastName"
                aria-label="You are required to enter your last name"
              />
              <Message for="lastName" />
            </Box>
            <Box
              width={[12 / 12, null, 5 / 12]}
              mr={[0, null, (2 / 12) * 100 + '%']}
              mb={10}
            >
              <Label>Phone number*</Label>
              <Input
                placeholder="Enter phone number"
                name="phone"
                aria-label="You are required to enter your phone number"
              />
              <Message for="phone" />
            </Box>
            <Box width={[12 / 12, null, 5 / 12]} mb={10}>
              <Label>Email address *</Label>
              <Input
                placeholder="Enter email address"
                name="email"
                aria-label="You are required to enter your email address"
              />
              <Message for="email" />
            </Box>
            <Box
              width={[12 / 12, null, 5 / 12]}
              mr={[0, null, (2 / 12) * 100 + '%']}
              mb={10}
            >
              <Label>Postcode*</Label>
              <Widget>
                <Input
                  placeholder="Enter postcode"
                  name="postcode"
                  aria-label="You are required to enter your postcode"
                />
                <Message for="postcode" />
              </Widget>
            </Box>
            <Box width={[12 / 12, null, 5 / 12]} mb={10}>
              <Label>Preferred date*</Label>
              <Widget>
                <Form.Field
                  placeholder="Enter date"
                  dropUp
                  parse={['L', 'l', 'll', 'LL']}
                  min={new Date()}
                  name="date"
                  aria-label="You are required to select a date"
                />
              </Widget>
              <Message for="date" />
            </Box>

            <Box width={[12 / 12, null, 12 / 12]} mb={10}>
              <Label bg={this.props.bg}>Enquiry type</Label>
              <Widget>
                <Form.Field
                  bg={this.props.bg}
                  placeholder="Enquiry Type"
                  name="enquiryType"
                  data={enquiryTypes}
                  type="dropdownlist"
                  aria-label="Select the reason for your enquiry"
                />
                <Message for="enquiryType" />
              </Widget>
            </Box>

            {!singleLocation && (
              <Box width={[12 / 12, null, 12 / 12]} mr={[0, null, 0]} mb={10}>
                <Label>
                  {this.props.brandSpecific
                    ? `Your nearest Citygate ${this.props.brand} Dealership`
                    : 'Nearest dealership*'}
                </Label>
                <Widget>
                  <Form.Field
                    placeholder="Choose a location"
                    name="location"
                    data={this.props.locations}
                    type="dropdownlist"
                    aria-label="You are required to select a dealership location"
                  />
                  <Message for="location" />
                </Widget>
              </Box>
            )}

            {singleLocation && (
              <Box width={[12 / 12, null, 12 / 12]} mr={[0, null, 0]} mb={10}>
                <Label>
                  Your nearest Citygate {this.props.brand} Dealership is{' '}
                  <a
                    href={`/locations/${this.props.location.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {this.props.location &&
                      this.props.location.locationInfo &&
                      this.props.location.locationInfo.location}
                  </a>
                </Label>
              </Box>
            )}
            <PermissionControls email sms phone />
            <Box width={12 / 12} mb={10}>
              <Label>
                <Checkbox
                  name="termsAndConditions"
                  aria-label="You are required to agree to our terms and conditions"
                />
                I agree to Citygate’s{' '}
                <Link to="/legal">terms and conditions</Link>
              </Label>
              <Message for="termsAndConditions" />
            </Box>
            <CenteredContentBox width={12 / 12} mt={35}>
              <SubmitButton type="submit" aria-label="Register Interest">
                Register Interest
              </SubmitButton>
            </CenteredContentBox>
          </Flex>
        </Fieldset>
      </Form>
    )
  }
}

FormTestDrive.propTypes = {
  locations: arrayOf(string),
  vehicleId: string,
  newVehicle: bool,
  brandSpecific: bool,
  brand: string
}

export default FormTestDrive
